import { useState, useEffect, useRef, useMemo } from 'react'
import { Box, Grid, TextField, Typography, InputAdornment, Card, useTheme } from '@mui/material'
import { ButtonBasic, CardServicioSoftland } from '../../../components'
import { showModal } from 'react-redux-modal-provider'
import ModalTicketsServicio from '../../../modals/ticket/ModalTicketsServicio'
import SearchIcon from '@mui/icons-material/Search';

const TabCuentaServicio = ({tratos}) => {
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';
    
    const [data, setData] = useState([]);
    const [dataFiltered, setDataFiltered] = useState([]);
    const [dataDisplayed, setDataDisplayed] = useState([]);
    const [displayCount, setDisplayCount] = useState(20);
    const [textSearch, setTextSearch] = useState('');
    const observerTarget = useRef(null);

    // Hook para lazy loading con Intersection Observer
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && displayCount < dataFiltered.length) {
                    setDisplayCount(prev => Math.min(prev + 20, dataFiltered.length));
                }
            },
            { threshold: 0.1 }
        );

        if (observerTarget.current) {
            observer.observe(observerTarget.current);
        }

        return () => {
            if (observerTarget.current) {
                observer.unobserve(observerTarget.current);
            }
        };
    }, [displayCount, dataFiltered.length]);

    // Actualizar servicios mostrados cuando cambia dataFiltered o displayCount
    useEffect(() => {
        setDataDisplayed(dataFiltered.slice(0, displayCount));
    }, [dataFiltered, displayCount]);

    // Resetear displayCount cuando cambia dataFiltered
    useEffect(() => {
        setDisplayCount(20);
    }, [dataFiltered]);

    const initialCard = () => {
        if(tratos?.length > 0){
            setData(tratos);
            setDataFiltered(tratos);
        }else{
            setData([])
            setDataFiltered([])
        }
    }

    useEffect(() => {
        initialCard()
    }, [tratos])

    // Filtrar servicios por búsqueda
    useEffect(() => {
        if (textSearch.trim() === '') {
            setDataFiltered(data);
        } else {
            const searchLower = textSearch.toLowerCase();
            const filtered = data.filter((servicio) => {
                return (
                    servicio?.contrato?.toLowerCase().includes(searchLower) ||
                    servicio?.nombre?.toLowerCase().includes(searchLower) ||
                    servicio?.direccion?.toLowerCase().includes(searchLower) ||
                    servicio?.plan?.toLowerCase().includes(searchLower)
                );
            });
            setDataFiltered(filtered);
        }
    }, [textSearch, data]);

    const onClickTk = (id) => {
        showModal(ModalTicketsServicio, {servicioId: id});
    }
    const styleInputHeight = useMemo(() => ({
        '& .MuiInputLabel-root':{
          color: isDarkMode ? '#b0bec5' : 'gray',
          fontSize: 15
        },
        '& fieldset': {
            borderRadius: 2,
            border: 1,
            borderColor: isDarkMode ? '#3d4a62' : 'gray',
            height: 45
        },
        '& label':{
          marginTop: -0.5,
        },
        '& .MuiOutlinedInput-root': {
          '&.Mui-focused fieldset': {
            border:1,
            height: 45,
            borderColor: isDarkMode ? '#90caf9' : '#0364ff',
          },
          '&:hover':{
            border: 1,
            borderRadius: 2,
            borderColor: isDarkMode ? '#3d4a62' : 'gray',
            height: 40
          },
          '&.Mui-focused': {
            '&:hover':{
              border:0,
              height: 40
            }
          },
          color: isDarkMode ? '#e0e0e0' : 'gray',
          height: 40
        },
        '& .MuiInputBase-input': {
            color: isDarkMode ? '#e0e0e0' : '#000',
        }
    }), [isDarkMode])
    return (
        <Box>
            {/* Barra de búsqueda y total */}
            <Card
                sx={{
                    mb: 2,
                    p: 2,
                    background: isDarkMode ? '#2d3a52' : '#ffffff',
                    border: `1px solid ${isDarkMode ? '#3d4a62' : '#e9ecef'}`,
                    borderRadius: 2,
                    boxShadow: 4
                }}
            >
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={8}>
                        <Typography sx={{color: isDarkMode ? '#b0bec5' : '#6b788e', fontSize: 14, mb: 0}}>
                            Buscar Servicio
                        </Typography>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Buscar por contrato, nombre, dirección..."
                            value={textSearch}
                            onChange={(e) => setTextSearch(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: isDarkMode ? '#b0bec5' : '#6b788e' }} />
                                    </InputAdornment>
                                ),
                            }}
                            sx={styleInputHeight}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4} display='flex' justifyContent='flex-end'>
                        <Box
                            sx={{
                                px: 2,
                                py: 1,
                                background: isDarkMode ? 'linear-gradient(135deg, #1a3a5c 0%, #0d1f3a 100%)' : 'linear-gradient(135deg, #0364ff 0%, #02378c 100%)',
                                borderRadius: 1.5,
                                boxShadow: isDarkMode ? '0 2px 6px rgba(26, 58, 92, 0.3)' : '0 2px 6px rgba(3, 100, 255, 0.2)'
                            }}
                        >
                            <Typography
                                sx={{
                                    color: '#fff',
                                    fontWeight: 700,
                                    fontSize: 14,
                                    textAlign: 'center'
                                }}
                            >
                                {dataFiltered?.length || '0'} servicio{dataFiltered?.length !== 1 ? 's' : ''}
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
            </Card>

            <Grid container spacing={1}>
                {Array.isArray(dataDisplayed) && dataDisplayed?.length > 0 && (
                    <>
                        {dataDisplayed?.map((preS)=>(
                            <Grid item xs={12} key={`${preS?.contrato}grid`}>
                                <CardServicioSoftland
                                    item={preS}
                                    key={preS?.contrato}
                                    onClickTk={onClickTk}
                                />
                            </Grid>
                        ))}
                    </>
                )}
                
                {/* Elemento observador para lazy loading */}
                {displayCount < dataFiltered.length && (
                    <Grid item xs={12}>
                        <Box 
                            ref={observerTarget}
                            sx={{
                                height: 20,
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                my: 2
                            }}
                        >
                            <Typography sx={{ color: isDarkMode ? '#b0bec5' : '#6b788e', fontSize: 14 }}>
                                Cargando más servicios...
                            </Typography>
                        </Box>
                    </Grid>
                )}

                {/* Mensaje cuando no hay resultados */}
                {dataFiltered?.length === 0 && (
                    <Grid item xs={12}>
                        <Box
                            sx={{
                                p: 4,
                                textAlign: 'center'
                            }}
                        >
                            <Typography sx={{ color: isDarkMode ? '#b0bec5' : '#6b788e', fontSize: 16, fontWeight: 600 }}>
                                {textSearch ? 'No se encontraron servicios con ese criterio de búsqueda' : 'No hay servicios disponibles'}
                            </Typography>
                        </Box>
                    </Grid>
                )}
            </Grid>
        </Box>
    )
}

export default TabCuentaServicio